import { PlaybookChapter, ChecklistData, PhaseData } from '@/types/gamePlan';

// Builds a self-contained, brand-styled print sheet for the Pre-Sale Launch
// Checklist and opens it in a new window to print. Standalone HTML (inline CSS)
// so it's independent of the app's DOM/styles — a sheet worth pinning to a
// fridge or desk, not a bland dump.

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function printChecklist(chapters: PlaybookChapter[]): void {
  const checklist = chapters.find((c) => c.id === 'checklist');
  const timeline = chapters.find((c) => c.id === 'gameplan');
  if (!checklist) return;

  const groups = checklist.sections
    .filter((s) => s.title !== 'Overview')
    .flatMap((s) => s.fragments)
    .map((f) => ({ title: f.label || f.title, items: (f.data as unknown as ChecklistData).items ?? [] }));

  const phases = (timeline?.sections.find((s) => s.title === 'The Timeline')?.fragments ?? []).map(
    (f) => f.data as unknown as PhaseData,
  );

  const progression = phases.length
    ? `<div class="prog">${phases
        .map(
          (p, i) =>
            `<div class="step"><div class="num">${esc(String(p.target))}</div><div class="rng">${esc(
              p.range,
            )}</div></div>${i < phases.length - 1 ? '<div class="arw">→</div>' : ''}`,
        )
        .join('')}</div>`
    : '';

  const groupsHtml = groups
    .map(
      (g, i) => `
      <section class="grp">
        <h2><span class="gnum">${i + 1}</span>${esc(g.title)}</h2>
        <ul>${g.items.map((it) => `<li><span class="box"></span><span>${esc(it)}</span></li>`).join('')}</ul>
      </section>`,
    )
    .join('');

  // Blank section for the owner's own to-dos — empty boxes + ruled write lines.
  const BLANK_ROWS = 12;
  const yourTodos = `
      <section class="grp blank">
        <h2><span class="gnum">+</span>Your To-Dos</h2>
        <ul>${Array.from({ length: BLANK_ROWS })
          .map(() => `<li><span class="box"></span><span class="writeline"></span></li>`)
          .join('')}</ul>
      </section>`;

  const html = `<!doctype html><html><head><meta charset="utf-8">
<title>Tee Box — Pre-Sale Launch Checklist</title>
<style>
  @page { margin: 14mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { margin: 0; font-family: ui-sans-serif, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
         color: #16241E; background: #FBF6EC; }
  .sheet { max-width: 760px; margin: 0 auto; padding: 8px; }
  .hdr { background: #0C3D2D; color: #FBF6EC; border-radius: 12px; padding: 22px 26px; position: relative; overflow: hidden; }
  .wm { font-weight: 800; letter-spacing: .18em; font-size: 12px; opacity: .8; }
  .eyebrow { font-size: 11px; letter-spacing: .22em; text-transform: uppercase; color: #E2D1A7; margin-top: 10px; }
  .hdr h1 { margin: 4px 0 0; font-size: 30px; font-weight: 800; letter-spacing: .01em; }
  .rule { height: 3px; width: 54px; background: #B58F2D; border-radius: 3px; margin: 12px 0 10px; }
  .sub { font-size: 13px; color: rgba(251,246,236,.82); }
  .prog { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin: 18px 0 4px;
          background: #FFFDF8; border: 1px solid #E6DFCF; border-radius: 10px; padding: 14px 18px; }
  .step { text-align: center; min-width: 52px; }
  .step .num { font-size: 22px; font-weight: 800; color: #B58F2D; line-height: 1; }
  .step .rng { font-size: 10px; color: #6C756D; margin-top: 3px; }
  .arw { color: #B58F2D; font-size: 16px; }
  .grp { margin-top: 20px; break-inside: avoid; page-break-inside: avoid; }
  .grp h2 { display: flex; align-items: center; gap: 10px; font-size: 15px; font-weight: 700; color: #0C3D2D;
            text-transform: uppercase; letter-spacing: .04em; margin: 0 0 10px; }
  .gnum { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px;
          border-radius: 50%; background: #0C3D2D; color: #FBF6EC; font-size: 12px; font-weight: 700; }
  .grp ul { list-style: none; margin: 0; padding: 0; }
  .grp li { display: flex; gap: 12px; align-items: flex-start; padding: 7px 0; font-size: 13.5px; line-height: 1.5;
            border-bottom: 1px dotted #E6DFCF; }
  .grp li:last-child { border-bottom: none; }
  .box { flex: none; width: 17px; height: 17px; border: 1.8px solid #0C3D2D; border-radius: 4px; margin-top: 1px; }
  .blank li { padding: 11px 0; border-bottom: none; }
  .writeline { flex: 1; border-bottom: 1px solid #C9BE9E; height: 16px; }
  .ftr { margin-top: 22px; padding-top: 12px; border-top: 1px solid #E6DFCF; display: flex;
         justify-content: space-between; font-size: 11px; color: #6C756D; }
  .ftr b { color: #0C3D2D; }
</style></head>
<body><div class="sheet">
  <div class="hdr">
    <div class="wm">TEE BOX</div>
    <div class="eyebrow">Owner Playbook</div>
    <h1>Pre-Sale Launch Checklist</h1>
    <div class="rule"></div>
    <div class="sub">Your 16-week run from signing to 50 Founding Members. Tick each one as you go.</div>
  </div>
  ${progression}
  ${groupsHtml}
  ${yourTodos}
  <div class="ftr"><span><b>Tee Box</b> · HQ Owner Playbook</span><span>One rhythm at a time. Fill the box.</span></div>
</div>
<script>window.onload=function(){setTimeout(function(){window.print();},250);};</script>
</body></html>`;

  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
}
